import * as ExcelJS from 'exceljs';

export interface ParsedFileResult {
  headers: string[];
  rows: string[][];
}

export class FileService {
  public static templateHeaders = [
    'CPF CNPJ',
    'Razão Social',
    'Email',
    'Nacionalidade',
    'Telefones',
    'Politicamente Exposto',
    'Data Nascimento',
    'Renda Mensal Declarada',
    'Patrimonio Declarado',
    'Atividade Principal / Profissão',
    'CNPJ Custodiante',
    'CEP',
    'Logradouro',
    'Complemento',
    'Bairro',
    'Cidade',
    'UF',
    'Numero'
  ];

  public static async parseXLSX(data: ArrayBuffer, template: string[]): Promise<ParsedFileResult> {
    const workbook = new ExcelJS.Workbook();
    return workbook.xlsx.load(data).then((workbook) => {
      const worksheet = workbook.worksheets[0];
      const headers: string[] = [];
      const rows: string[][] = [];

      worksheet.eachRow((row, rowNumber) => {
        const rowValues = Array.isArray(row.values) ? row.values.slice(1) : [];
        if (rowNumber === 1) {
          headers.push(...rowValues.map((cell) => this.getCellValue(cell).trim() || ''));
        } else {
          rows.push(
            rowValues.map((cell) => (cell ? this.getCellValue(cell).trim() : '')),
          );
        }
      });

      this.checkHeaders(headers, template);

      return { headers, rows };
    });
  }

  public static checkHeaders(headers: string[], template: string[]): void {
    const missingHeaders = template.filter(
      (templateHeader) => !headers.includes(templateHeader),
    );
    if (missingHeaders.length > 0) {
      throw new Error(
        `Cabeçalho da planilha inválido, falta(m) ${missingHeaders.join(
          ', '
        )}`
      );
    }
  }

  public static getCellValue(cell: any): string {
    if (!cell) return '';

    if (cell instanceof Date) {
      const day = cell.getUTCDate().toString().padStart(2, '0');
      const month = (cell.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = cell.getUTCFullYear();
      return `${day}/${month}/${year}`;
    }

    if (typeof cell === 'object') {
      if (cell.text || cell.text === '') return cell.text;
      if (cell.hyperlink) return cell.hyperlink;
      if (cell.value !== undefined) return this.getCellValue(cell.value);
    }

    return cell.toString();
  }

  public static formatDate(dateString: string) {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}T00:00:00Z`;
  }

  public static async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as ArrayBuffer);
        } else {
          reject(new Error('Falha ao ler o arquivo'));
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
      reader.readAsArrayBuffer(file);
    });
  }

  public static async readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        } else {
          reject(new Error('Falha ao ler o arquivo'));
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
      reader.readAsText(file);
    });
  }

  public static async parseFile(file: File, template: string[]): Promise<ParsedFileResult> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      return this.parseXLSX(arrayBuffer, template);
    } else {
      throw new Error('Formato de arquivo não suportado. Por favor, use XLSX ou XLS (Utilize a planilha modelo).');
    }
  }
}
